import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, MapPin, ArrowRight, Ticket, ChevronRight } from 'lucide-react';
import { userApi } from '../../config';

const GRADIENTS = [
  'linear-gradient(135deg, var(--forest), var(--forest-dark))',
  'linear-gradient(135deg, var(--indigo), #3d4568)',
  'linear-gradient(135deg, var(--rose), #7f3a48)',
  'linear-gradient(135deg, var(--marigold-dark), var(--forest))',
  'linear-gradient(135deg, var(--forest-dark), var(--indigo))',
  'linear-gradient(135deg, #3d4568, var(--rose))'
];

interface TreatmentRetreat {
  _id: string;
  packageName: string;
  city: string;
  state: string;
  duration: string;
  price: number;
  category: string;
  availableSlots?: number;
  availabilityFrom?: string;
  availabilityTo?: string;
  uploadImages?: string[];
}

// Clean up any test/conversational prefix or suffix in package titles
const cleanPackageName = (name: string) => {
  if (!name) return '';
  return name.replace(/^Great!\s*₹[\d,]+\s*/i, '').replace(/\s*Mein$/i, '').trim();
};

// Format price in full real price format (e.g. ₹11,500)
const formatOnwardsPrice = (val: number | string) => {
  const num = typeof val === 'number' ? val : Number(String(val).replace(/[^0-9]/g, '')) || 0;
  return `₹${num.toLocaleString('en-IN')}`;
};

// Lotus Flower SVG Icon for Card Headers
const LotusIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12 3C11.3 6 9.5 8.8 7 10.8C9.5 11.5 12 11.5 12 11.5C12 11.5 14.5 11.5 17 10.8C14.5 8.8 12.7 6 12 3Z" />
    <path d="M5.5 11.8C3.2 13.2 1.8 15.6 2.5 17.6C4.5 18 7.5 17 10 15C8.2 14.1 6.6 13 5.5 11.8Z" />
    <path d="M18.5 11.8C20.8 13.2 22.2 15.6 21.5 17.6C19.5 18 16.5 17 14 15C15.8 14.1 17.4 13 18.5 11.8Z" />
    <path d="M6.8 18.2C8.3 19.3 10.1 19.8 12 19.8C13.9 19.8 15.7 19.3 17.2 18.2C15 17.3 9 17.3 6.8 18.2Z" opacity="0.9" />
  </svg>
);

const CardImage: React.FC<{ src: string; alt: string; className?: string }> = ({ src, alt, className = '' }) => {
  return (
    <div className={`relative w-full h-full overflow-hidden bg-[#F5EFE6] ${className}`}>
      {/* Blurred background: fills any tiny aspect-ratio difference softly without cutoffs */}
      <img
        src={src}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full scale-110 object-cover blur-md opacity-35 select-none pointer-events-none"
      />
      {/* Contained main image: guarantees NO edges or text get clipped on the sides */}
      <img
        src={src}
        alt={alt}
        className="relative z-10 w-full h-full object-contain"
      />
    </div>
  );
};

const TreatmentsSection: React.FC = () => {
  const [programs, setPrograms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTreatments = async () => {
      try {
        const response = await userApi.get<any>('/treatment-retreats');
        if (response.data && response.data.success && response.data.data) {
          const rawData: TreatmentRetreat[] = response.data.data;

          const formattedPrograms: any[] = [];

          rawData.forEach((item, index) => {
            const isRetreat = item.category?.toLowerCase().includes('retreat') || false;
            const grad = GRADIENTS[index % GRADIENTS.length];
            const loc = `${item.city || ''}${item.city && item.state ? ', ' : ''}${item.state || ''}`;
            const image = Array.isArray(item.uploadImages) && item.uploadImages.length > 0 ? item.uploadImages[0] : '';
            const spots = item.availableSlots || 0;

            formattedPrograms.push({
              id: item._id,
              name: item.packageName,
              loc: loc || 'Various Locations',
              dur: item.duration || (isRetreat ? 'Multiple days' : 'Flexible duration'),
              rawPrice: item.price || 0,
              price: `₹${item.price?.toLocaleString() || 0}`,
              spots: isRetreat ? spots : 0,
              grad,
              image
            });
          });

          setPrograms(formattedPrograms);
        }
      } catch (error) {
        console.error('Error fetching treatments and retreats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTreatments();
  }, []);

  if (loading) {
    return (
      <section className="py-20 bg-[#FFFFFF] flex justify-center items-center min-h-[300px]">
        <p className="text-[#3B5750] font-medium">Loading wellness programs...</p>
      </section>
    );
  }

  if (programs.length === 0) {
    return null;
  }

  return (
    <section id="treatments" className="pt-2 sm:pt-3 pb-12 sm:pb-16 bg-[#FFFFFF] relative overflow-hidden">


      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-between gap-3 mb-4 sm:mb-6"
        >
          <div>
            <h2 className="font-semibold text-[16px] md:text-[18px] text-slate-900 leading-tight m-0 font-bold">
              Wellness treatments &amp; planned retreats
            </h2>
          </div>
          <button
            onClick={() => navigate('/services')}
            className="inline-flex items-center gap-1.5 text-[13px] sm:text-[14px] font-semibold text-slate-900 hover:text-[#1E443B] transition-colors cursor-pointer shrink-0 whitespace-nowrap bg-transparent border-0 p-0 shadow-none outline-none"
          >
            View All <ArrowRight size={15} strokeWidth={2.2} />
          </button>
        </motion.div>

        {/* Cards Panel - Compact sizing matching BookMyAdventure */}
        <div className="flex flex-wrap gap-3.5 sm:gap-4">
          {programs.map((p, idx) => (
            <div
              key={p.id || idx}
              onClick={() => navigate(`/luxury-wellness-retreats/${p.id}`)}
              className="group bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs hover:shadow-md transition-all duration-300 hover:-translate-y-1 cursor-pointer flex flex-col w-[calc(50%-0.5rem)] sm:w-[240px] md:w-[260px] max-w-[280px]"
            >
              {/* Thumbnail Header */}
              <div className="aspect-[16/9] relative overflow-hidden w-full bg-slate-100">
                {p.image ? (
                  <CardImage src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full" style={{ background: p.grad }} />
                )}

                {/* Duration Badge */}
                {p.dur && (
                  <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-xs text-white text-[9.5px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1 z-20 border border-white/10 shadow-xs">
                    <Clock className="w-2.5 h-2.5 text-[#FF6B35]" />
                    <span>{p.dur}</span>
                  </div>
                )}

                {/* Spots Left */}
                {p.spots > 0 && (
                  <div className="absolute top-2 right-2 bg-[#E6A93F] text-[#15302B] font-mono text-[8.5px] font-bold px-1.5 py-0.5 rounded-full shadow-sm z-20">
                    {p.spots} spots left
                  </div>
                )}
              </div>

              {/* Card Body */}
              <div className="p-2.5 sm:p-3 flex flex-col bg-white">
                {/* Title */}
                <h3 className="treatment-card-title font-semibold text-[13px] text-slate-900 line-clamp-1 group-hover:text-[#FF6B35] transition-colors leading-snug">
                  {cleanPackageName(p.name)}
                </h3>

                {/* Location with Orange MapPin */}
                <div className="treatment-card-loc flex items-center gap-1 text-[11px] text-slate-500 mt-1">
                  <MapPin className="w-3 h-3 text-[#FF6B35] shrink-0 fill-[#FF6B35]/20" />
                  <span className="truncate">{p.loc}</span>
                </div>

                {/* Price Row with Ticket icon */}
                <div className="treatment-card-price flex items-center gap-1 text-slate-800 font-semibold text-[12px] mt-1">
                  <Ticket className="w-2.5 h-2.5 text-slate-400 shrink-0" />
                  <span>{formatOnwardsPrice(p.rawPrice || p.price)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TreatmentsSection;
